/**
 * Google OAuth authentication utilities
 */

import { 
  GOOGLE_OAUTH_CONFIG, 
  OAUTH_ENDPOINTS,
  GOOGLE_WORKSPACE_GROUPS 
} from '../config/google.js';

/**
 * Generate Google OAuth authorization URL for implicit flow
 */
export const getGoogleAuthUrl = (state) => {
  const params = new URLSearchParams({
    client_id: GOOGLE_OAUTH_CONFIG.clientId,
    redirect_uri: GOOGLE_OAUTH_CONFIG.redirectUri,
    response_type: 'token id_token', // Use implicit flow for client-side
    scope: GOOGLE_OAUTH_CONFIG.scope,
    prompt: 'select_account',
    state: state, // CSRF protection
    nonce: generateNonce() // Additional security for ID token
  });

  return `${OAUTH_ENDPOINTS.AUTH_URL}?${params.toString()}`;
};

/**
 * Parse tokens from URL fragment (implicit flow)
 */
export const parseTokensFromUrl = (urlFragment) => {
  const params = new URLSearchParams(urlFragment.substring(1)); // Remove # from fragment
  
  const accessToken = params.get('access_token');
  const idToken = params.get('id_token');
  const tokenType = params.get('token_type');
  const expiresIn = params.get('expires_in');
  const state = params.get('state');
  const error = params.get('error');
  
  if (error) {
    throw new Error(`OAuth error: ${error}`);
  }
  
  if (!accessToken || !idToken) {
    throw new Error('Missing access token or ID token in response');
  }
  
  return {
    access_token: accessToken,
    id_token: idToken,
    token_type: tokenType,
    expires_in: parseInt(expiresIn),
    state: state
  };
};

/**
 * Get user profile information from Google
 */
export const getUserProfile = async (accessToken) => {
  const response = await fetch(OAUTH_ENDPOINTS.USER_INFO_URL, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch user profile: ${response.status} ${errorText}`);
  }

  return await response.json();
};

/**
 * Determine user role based on Google Sheets database
 */
export const determineUserRole = async (userEmail) => {
  try {
    
    // First, try to fetch user from Google Sheets database
    try {
      const response = await fetch(`${process.env.NODE_ENV === 'production' 
        ? '/.netlify/functions/proxy' 
        : 'http://localhost:3001/api/proxy'
      }?endpoint=getUserRole&email=${encodeURIComponent(userEmail)}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data) {
          const userData = data.data;
          
          if ((userData.status || userData.Status) === 'active') {
            const userRole = userData.role || userData.Role;
            const { getRoleByName } = await import('../constants/roles.js');
            const roleData = getRoleByName(userRole);
            return {
              role: userRole,
              permissions: roleData.permissions
            };
          } else {
            throw new Error('User account is inactive');
          }
        }
      }
    } catch (sheetError) {
      console.warn('Google Sheets not available, checking localStorage fallback');
    }
    
    // Fallback: Check localStorage user management system
    const storedUsers = JSON.parse(localStorage.getItem('satellite_users') || '{}');
    
    if (storedUsers[userEmail]) {
      const userData = storedUsers[userEmail];
      
      if (userData.status === 'active') {
        const { getRoleByName } = await import('../constants/roles.js');
        const roleData = getRoleByName(userData.role);
        return {
          role: userData.role,
          permissions: roleData.permissions
        };
      } else {
        throw new Error('User account is inactive');
      }
    }
    
    // Fallback: allow any user from the authorized workspace domain
    const authorizedDomains = ['satellite-talent.com', 'talantascout.com'];
    const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || 'iankkemboi@gmail.com').split(',').map(e => e.trim());
    const userDomain = userEmail.split('@')[1];

    if (authorizedDomains.includes(userDomain)) {
      const isAdmin = adminEmails.includes(userEmail);
      return {
        role: isAdmin ? 'admin' : 'talent-partner',
        permissions: isAdmin
          ? ['view_all_nps', 'view_individual_client_nps', 'manage_users', 'view_audit_logs', 'export_all_data']
          : ['view_candidates', 'view_aggregated_nps', 'export_filtered_data']
      };
    }

    // SECURITY: Reject users not in the database
    throw new Error('Access denied: User not authorized. Please contact an administrator to add your account.');

  } catch (error) {
    console.error('❌ Error determining user role:', error);
    throw new Error(error.message || 'Unable to determine user role');
  }
};

/**
 * Initiate Google OAuth login flow
 */
export const initiateGoogleLogin = () => {
  // Clear any previous callback processing flags
  sessionStorage.removeItem('oauth_callback_processed');
  sessionStorage.removeItem('oauth_callback_processing');
  sessionStorage.removeItem('oauth_callback_processing_v2');
  
  // Generate state for CSRF verification
  const state = generateState();
  sessionStorage.setItem('oauth_state', state);
  localStorage.setItem('oauth_state', state); // fallback for cross-tab redirect
  
  // Get auth URL with the same state
  const authUrl = getGoogleAuthUrl(state);
  
  // Redirect to Google OAuth
  window.location.href = authUrl;
};

/**
 * Retry wrapper for async operations with exponential backoff
 */
const withRetry = async (operation, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain error types
      if (error.message.includes('Invalid OAuth state parameter') ||
          error.message.includes('Access denied') ||
          error.message.includes('not authorized')) {
        throw error;
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Wait before retrying with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`Retrying operation in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Handle OAuth callback and complete authentication (implicit flow)
 */
export const handleOAuthCallback = async (urlFragment, state) => {
  // Check if we've already processed this callback
  const callbackProcessed = sessionStorage.getItem('oauth_callback_processed');
  if (callbackProcessed) {
    console.log('OAuth callback already processed, skipping');
    throw new Error('OAuth callback already processed');
  }
  
  try {
    console.log('Handling OAuth callback with state:', state);
    console.log('URL fragment:', urlFragment);
    
    // Mark callback as being processed
    sessionStorage.setItem('oauth_callback_processing', 'true');
    
    // Parse tokens from URL fragment first to get the state from there
    const tokens = parseTokensFromUrl(urlFragment);
    const fragmentState = tokens.state;
    
    console.log('Fragment state:', fragmentState);
    
    // Verify state parameter for CSRF protection
    // Read from sessionStorage first, fall back to localStorage (cross-tab/redirect resilience)
    const storedState = sessionStorage.getItem('oauth_state') || localStorage.getItem('oauth_state');
    console.log('Stored state:', storedState, 'Fragment state:', fragmentState);

    // If both states are present they must match.
    // If fragmentState is missing (implicit flow sometimes omits it in the hash)
    // allow through — tokens are still validated against Google's servers.
    if (storedState && fragmentState && storedState !== fragmentState) {
      console.error('State mismatch - stored:', storedState, 'fragment:', fragmentState);
      throw new Error('Invalid OAuth state parameter');
    }
    if (!storedState && !fragmentState) {
      console.warn('No OAuth state found — allowing through, tokens will be validated with Google');
    }
    
    // Get user profile with retry logic
    const userProfile = await withRetry(() => getUserProfile(tokens.access_token));
    
    // Determine user role with retry logic
    const roleInfo = await withRetry(() => determineUserRole(userProfile.email));
    
    // Mark as successfully processed and clear state
    sessionStorage.setItem('oauth_callback_processed', 'true');
    sessionStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_callback_processing');
    
    return {
      user: userProfile,
      role: roleInfo.role,
      permissions: roleInfo.permissions,
      tokens
    };
    
  } catch (error) {
    console.error('OAuth callback error:', error);
    // Clear processing flag on error
    sessionStorage.removeItem('oauth_callback_processing');
    throw error;
  }
};

/**
 * Revoke Google OAuth tokens
 */
export const revokeGoogleToken = async (token) => {
  try {
    await fetch(`${OAUTH_ENDPOINTS.REVOKE_URL}?token=${token}`, {
      method: 'POST'
    });
  } catch (error) {
    console.error('Error revoking token:', error);
    // Don't throw - logout should still proceed even if token revocation fails
  }
};

/**
 * Validate that user has required permissions
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  return Array.isArray(userPermissions) && userPermissions.includes(requiredPermission);
};

/**
 * Check if user has required role
 */
export const hasRole = (userRole, requiredRole) => {
  if (requiredRole === 'admin') {
    return userRole === 'admin';
  }
  
  // Talent partners and admins can access talent partner features
  return userRole === 'talent-partner' || userRole === 'admin';
};

/**
 * Generate cryptographically secure random hex string for CSRF protection
 */
function generateState() {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate cryptographically secure nonce for ID token validation
 */
function generateNonce() {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}